import { useState, useEffect, useRef } from "react";
import axios from "axios";
import moment from "moment";
import Cookies from "js-cookie";

import DefaultPageSize from "./Table/DefaultPageSize";

// stable regardless of key order, array order and empty values (null / "" / [])
const getSearchSignature = (params) =>
    JSON.stringify(
        Object.keys(params)
            .filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== "" && !(Array.isArray(params[key]) && params[key].length === 0))
            .sort()
            .map((key) => [key, Array.isArray(params[key]) ? [...params[key]].sort() : params[key]])
    );

const DataFetchHookV3 = (
    initialurl,
    requestMethod = "POST",
    defaultRequestData = { page: 1, size: 20, order: "DESC" },
    firstTimeAutoLoad = true,
    maxRange = 3,
    rangeType = "months",
    { countOncePerSearch = false } = {}
) => {
    const [data, setData] = useState({ datalist: [] });
    const [totalResult, setTotalResult] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [isError, setIsError] = useState(false);
    const [url, setUrl] = useState(initialurl);
    const [requestData, setRequestData] = useState({ ...defaultRequestData, size: DefaultPageSize() });
    const [method] = useState(requestMethod);
    const [maxDateRange, setMaxDateRange] = useState(maxRange); // eslint-disable-line
    const [dateRangeType, setDateRangeType] = useState(rangeType); // eslint-disable-line
    const [isUpdated, setIsUpdated] = useState(false);
    const [isFirstTimeAutoLoad, setIsFirstTimeAutoLoad] = useState(firstTimeAutoLoad);
    const [isExact, setIsExact] = useState(true);
    const [countError, setCountError] = useState(false);
    // search signature (without page/size) of the current total
    const countedSignature = useRef(null);

    const DATE_FORMAT = "YYYY-MM-DD";

    const getStartDate = () =>
        requestData && requestData.startDate ? requestData.startDate.format(DATE_FORMAT) : moment().subtract(maxDateRange, dateRangeType).format(DATE_FORMAT);

    const getEndDate = () => (requestData && requestData.endDate ? requestData.endDate.format(DATE_FORMAT) : moment().format(DATE_FORMAT));

    const getPage = () => (requestData && requestData.page ? requestData.page : 1);
    const getSize = () => (requestData && requestData.size ? requestData.size : 20);
    const getOrder = () => (requestData && requestData.order === "ASC" ? requestData.order : "DESC");

    useEffect(() => {
        const fetchdata = async () => {
            setIsLoading(true);
            setIsError(false);
            try {
                let result;
                let options = { headers: { Authorization: "Bearer " + Cookies.get("access_token"), Authuser: Cookies.get("authuser") } };
                let param = { ...requestData, date_from: getStartDate(), date_to: getEndDate(), page: getPage(), size: getSize(), order: getOrder() };

                if (param.startDate && param.endDate) {
                    delete param.startDate;
                    delete param.endDate;
                }

                let signature = null;
                if (countOncePerSearch) {
                    // sort/paging don't change the total
                    const { page, size, offset, include_count, order, sort_field, ...searchParams } = param; // eslint-disable-line no-unused-vars
                    signature = getSearchSignature(searchParams);
                    param.include_count = signature !== countedSignature.current;
                }

                if (method === "POST") {
                    result = await axios
                        .post(url, param, options)
                        .then((result) => result)
                        .catch((error) => ({ data: false }));
                } else {
                    result = await axios
                        .get(url, options)
                        .then((result) => result)
                        .catch((error) => ({ data: false }));
                }
                if (result.data) {
                    if (result.data.list) {
                        setData({ datalist: result.data.list });
                    }
                    if (countOncePerSearch && result.data.countError) {
                        // total unknown: show a lower bound from this page; signature not stored, so the next request recounts
                        const listLength = (result.data.list || []).length;
                        const loaded = (Number(param.page) - 1) * Number(param.size) + listLength;
                        const hasMore = listLength === Number(param.size);
                        setTotalResult(hasMore ? loaded + 1 : loaded);
                        setIsExact(!hasMore);
                        setCountError(true);
                        setIsUpdated(moment().unix());
                    } else if (result.data.totalResult >= 0) {
                        setTotalResult(result.data.totalResult);
                        setIsExact(result.data.isExact !== false);
                        setCountError(false);
                        setIsUpdated(moment().unix());
                        if (countOncePerSearch) {
                            countedSignature.current = signature;
                        }
                    }
                    if (result.data.msg) {
                        setErrorMsg(result.data);
                        setIsError(true);
                    }
                } else {
                    setIsError(true);
                }
            } catch (err) {
                console.log(err);
                setIsError(true);
            }
            setIsLoading(false);
        };
        if (isFirstTimeAutoLoad) {
            fetchdata();
        } else {
            setIsFirstTimeAutoLoad(true);
        }
    }, [url, requestData]); // eslint-disable-line react-hooks/exhaustive-deps

    // force a fresh count on the next request
    const resetCount = () => {
        countedSignature.current = null;
    };

    return [{ data, totalResult, requestData, isLoading, isError, errorMsg, isUpdated, isExact, countError }, setUrl, setRequestData, resetCount];
};
export default DataFetchHookV3;
